import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FiInfo, FiX, FiChevronDown, FiSearch } from 'react-icons/fi';
import './CompanyScopeSelect.scss';

/**
 * Company scope selector — the standard "Tüm Şirketler / Belirli Şirketler"
 * control used by every company-scoped configuration (definitions, form-field
 * schemas, workflows). Rendered as a compact single-row bar that sits at the
 * TOP of a definition form: the scope is the first choice the user makes, and
 * the fields/pickers below it are filtered by it (see isInScope).
 *
 * Value contract mirrors the backend: an EMPTY array means "All Companies";
 * a non-empty array restricts the record to the checked companies.
 *
 * Props:
 *   value      — array of company ids ([] = All Companies)
 *   onChange   — (nextIds: string[]) => void
 *   companies  — [{ _id, name, isActive }]
 *   disabled   — optional
 *   hideLabel  — optional; for hosts that already render a "Company Scope"
 *                heading right above the control (e.g. the schema scope modal)
 *   restrictToCompanyIds — optional; ids the current user may scope to. When
 *                set, "All Companies" is off the table and the company list is
 *                limited to these ids (used when a non-admin creates a record:
 *                they may only define within their own company).
 */
export default function CompanyScopeSelect({ value, onChange, companies, disabled, hideLabel, restrictToCompanyIds }) {
  const { t } = useTranslation();
  const ids = Array.isArray(value) ? value.map(String) : [];
  const allCompanies = ids.length === 0;
  const restricted = Array.isArray(restrictToCompanyIds) && restrictToCompanyIds.length > 0;
  const restrictSet = restricted ? restrictToCompanyIds.map(String) : null;
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const pickerRef = useRef(null);

  // Close the company dropdown on any click outside of it; a fresh open
  // always starts with an empty search.
  useEffect(() => {
    if (!open) return;
    setQuery('');
    const onDown = (e) => {
      if (pickerRef.current && !pickerRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [open]);

  const toggleCompany = (id) => {
    const key = String(id);
    const next = ids.includes(key) ? ids.filter(x => x !== key) : [...ids, key];
    // A restricted user must always stay scoped to something — an empty
    // selection would mean "All Companies", which they may not choose.
    if (restricted && next.length === 0) return;
    onChange(next);
  };

  const options = (companies || [])
    .filter(c => c.isActive !== false || ids.includes(String(c._id)))
    .filter(c => !restricted || restrictSet.includes(String(c._id)) || ids.includes(String(c._id)));
  const nameOf = (id) => options.find(c => String(c._id) === id)?.name || t('companyScope.specificCompanies');

  const q = query.trim().toLocaleLowerCase();
  const visibleOptions = q
    ? options.filter(c => (c.name || '').toLocaleLowerCase().includes(q))
    : options;

  return (
    <div className={`company-scope ${disabled ? 'is-disabled' : ''}`}>
      {!hideLabel && (
        <span className="company-scope__label">
          {t('companyScope.label')}
          <span
            className="company-scope__info"
            title={`${t('companyScope.allCompaniesDesc')} / ${t('companyScope.specificCompaniesDesc')}`}
          >
            <FiInfo />
          </span>
        </span>
      )}

      <div className="company-scope__modes" role="radiogroup" aria-label={t('companyScope.label')}>
        <label
          className={`company-scope__mode ${allCompanies ? 'is-active' : ''} ${restricted ? 'is-unavailable' : ''}`}
          title={restricted ? t('companyScope.restrictedHint') : t('companyScope.allCompaniesDesc')}
        >
          <input
            type="radio"
            name="company-scope-mode"
            checked={allCompanies}
            disabled={disabled || restricted}
            onChange={() => { setOpen(false); onChange([]); }}
          />
          <span>{t('companyScope.allCompanies')}</span>
        </label>
        <label className={`company-scope__mode ${!allCompanies ? 'is-active' : ''}`} title={t('companyScope.specificCompaniesDesc')}>
          <input
            type="radio"
            name="company-scope-mode"
            checked={!allCompanies}
            disabled={disabled || options.length === 0}
            onChange={() => {
              // Switching to "specific" needs at least one selection to be
              // meaningful — preselect the first company, and open the picker
              // so the user immediately sees where to adjust it.
              if (ids.length === 0 && options.length > 0) {
                onChange([String(options[0]._id)]);
                setOpen(true);
              }
            }}
          />
          <span>{t('companyScope.specificCompanies')}</span>
        </label>
      </div>

      {!allCompanies && (
        <div className="company-scope__picker" ref={pickerRef}>
          <button
            type="button"
            className="company-scope__control"
            onClick={() => setOpen(o => !o)}
            disabled={disabled}
            aria-expanded={open}
            aria-haspopup="listbox"
          >
            <span className="company-scope__chips">
              {ids.map(id => (
                <span key={id} className="company-scope__chip">
                  {nameOf(id)}
                  <span
                    role="button"
                    tabIndex={0}
                    className="company-scope__chip-x"
                    aria-label={t('common.delete', 'Remove')}
                    onClick={(e) => { e.stopPropagation(); toggleCompany(id); }}
                    onKeyDown={(e) => { if (e.key === 'Enter') { e.stopPropagation(); toggleCompany(id); } }}
                  >
                    <FiX />
                  </span>
                </span>
              ))}
            </span>
            <FiChevronDown className={`company-scope__caret ${open ? 'is-open' : ''}`} />
          </button>

          {open && (
            <div className="company-scope__menu" role="listbox" aria-multiselectable="true">
              <div className="company-scope__search">
                <FiSearch />
                <input
                  type="text"
                  value={query}
                  autoFocus
                  placeholder={t('common.search', 'Search…')}
                  onChange={(e) => setQuery(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Escape') setOpen(false); }}
                />
              </div>
              <div className="company-scope__options">
                {visibleOptions.map(c => (
                  <label key={c._id} className={`company-scope__option ${ids.includes(String(c._id)) ? 'is-checked' : ''}`}>
                    <input
                      type="checkbox"
                      checked={ids.includes(String(c._id))}
                      disabled={disabled}
                      onChange={() => toggleCompany(c._id)}
                    />
                    <span>{c.name}</span>
                  </label>
                ))}
                {visibleOptions.length === 0 && (
                  <span className="company-scope__empty">
                    {options.length === 0 ? t('companyScope.noCompanies') : t('companyScope.noMatches', 'No matching companies')}
                  </span>
                )}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// Who may scope a definition to "All Companies": the tenant owner and tenant
// admins. Everyone else defines records only within their own company.
export function canScopeAllCompanies(user) {
  return ['tenant_owner', 'tenant_admin'].includes(user?.role);
}

// Default scope for a NEW record created by `user`: admins start at
// "All Companies" ([]), everyone else starts pinned to their own company.
export function defaultScopeForUser(user) {
  if (canScopeAllCompanies(user)) return [];
  const cid = user?.companyId?._id || user?.companyId;
  return cid ? [String(cid)] : [];
}

// Scope-compatibility rule for dependent pickers: a referenced definition
// (main group, schema, workflow…) is selectable for a record scoped to
// `scopeIds` only if the definition is visible to every company the record
// covers. An all-companies definition ([]) fits any scope; an all-companies
// record ([]) can only reference all-companies definitions.
export function isInScope(itemCompanyIds, scopeIds) {
  const itemIds = (itemCompanyIds || []).map(v => String(v?._id || v));
  if (itemIds.length === 0) return true;
  const scope = (scopeIds || []).map(String);
  if (scope.length === 0) return false;
  return scope.every(id => itemIds.includes(id));
}

// Render helper for list screens: the scope of a record as display text.
export function companyScopeLabel(companyIds, companies, t) {
  const ids = (companyIds || []).map(v => String(v?._id || v));
  if (ids.length === 0) return t('companyScope.allCompanies');
  const names = ids
    .map(id => (companies || []).find(c => String(c._id) === id)?.name)
    .filter(Boolean);
  return names.length ? names.join(', ') : t('companyScope.specificCompanies');
}
